import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { X, ExternalLink, FileText, Upload, File, CheckCircle, AlertTriangle, Plus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const STATUS_COLOR = {
  aguardando:           '#60a5fa',
  aguardando_pagamento: '#fb923c',
  pagamento_parcial:    '#fb923c',
  pago:                 '#4ade80',
};

const STATUS_LABEL = {
  aguardando:           'Aguardando Financeiro',
  aguardando_pagamento: 'Aguardando Pagamento',
  pagamento_parcial:    'Pagamento Parcial',
  pago:                 'Pago',
};

const fmt = (v) =>
  v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—';
const fmtN = (v) =>
  v != null ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '';

const CUSTO_FIELDS = [
  { key: 'custo_equipamento',       label: 'Equipamento' },
  { key: 'custo_instalacao',        label: 'Instalação' },
  { key: 'custo_indicacao',         label: 'Indicação' },
  { key: 'custo_logistica',         label: 'Logística' },
  { key: 'custo_material_eletrico', label: 'Material Elétrico' },
  { key: 'custo_crt',               label: 'CRT' },
  { key: 'custo_padrao',            label: 'Padrão' },
  { key: 'custo_brinde',            label: 'Brinde' },
];

const S = {
  heading:  { fontSize: 22, fontWeight: 700, color: '#f6c90e', marginBottom: 4 },
  sub:      { fontSize: 13, color: '#475569', marginBottom: 24 },
  tableWrap:{ background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12, overflowX: 'auto' },
  table:    { width: '100%', borderCollapse: 'collapse', minWidth: 950 },
  th:       { padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #1a2035', whiteSpace: 'nowrap' },
  td:       { padding: '12px 14px', fontSize: 13, color: '#cbd5e1', borderBottom: '1px solid #0f1117', whiteSpace: 'nowrap' },
  tag:      (c) => ({ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${c}22`, color: c, border: `1px solid ${c}44` }),
  btn:      (c = '#60a5fa', disabled = false) => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7,
    background: disabled ? '#1a2035' : `${c}22`, color: disabled ? '#475569' : c,
    fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    border: `1px solid ${disabled ? '#2d3748' : c + '44'}`,
  }),
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:    { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 14, padding: 28, width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' },
  label:    { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5, display: 'block' },
  input:    { width: '100%', padding: '9px 11px', borderRadius: 7, background: '#0f1117', border: '1px solid #1a2035', color: '#e2e8f0', fontSize: 13, outline: 'none', marginBottom: 12, boxSizing: 'border-box' },
  inputRO:  { width: '100%', padding: '9px 11px', borderRadius: 7, background: '#080e1a', border: '1px solid #0f1a2e', color: '#64748b', fontSize: 13, marginBottom: 12, boxSizing: 'border-box', cursor: 'default' },
  sel:      { width: '100%', padding: '9px 11px', borderRadius: 7, background: '#0f1117', border: '1px solid #1a2035', color: '#e2e8f0', fontSize: 13, outline: 'none', marginBottom: 12 },
  row2:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  divider:  { borderColor: '#1a2035', margin: '14px 0' },
  totalBox: { background: '#0f1117', borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  infoBox:  { background: '#0f1117', border: '1px solid #1a2035', borderRadius: 8, padding: '12px 14px', marginBottom: 14 },
  infoRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  payRow:   { background: '#080c14', border: '1px solid #1a2035', borderRadius: 8, padding: '10px 14px', marginBottom: 8 },
  newPayBox:{ background: 'rgba(246,201,14,0.04)', border: '1px solid rgba(246,201,14,0.2)', borderRadius: 10, padding: '14px 16px', marginTop: 12 },
};

const linkStyle = { background:'none', border:'none', color:'#e2e8f0', fontWeight:700, cursor:'pointer', padding:0, fontSize:'inherit', textDecoration:'underline', textDecorationColor:'rgba(226,232,240,0.3)', textUnderlineOffset:2 };

function calcCustoTotal(form, comissaoCalc, incluirComissao) {
  const campos = CUSTO_FIELDS.map(f => parseFloat(String(form[f.key] || '').replace(',', '.')) || 0);
  const comissao = incluirComissao ? (parseFloat(comissaoCalc) || 0) : 0;
  return campos.reduce((a, b) => a + b, 0) + comissao;
}

/* ── Modal de Pagamento ─────────────────────────────────────────────────────── */
function PagamentoModal({ item, onClose, onUpdated }) {
  const nfInputRef = useRef();
  const compInputRef = useRef();

  const [localItem, setLocalItem] = useState(item);
  const [uploadingNf, setUploadingNf] = useState(false);
  const [novoPag, setNovoPag] = useState({ valor: '', data_hora: '', comprovante: null });
  const [savingPag, setSavingPag] = useState(false);

  const pagamentos = localItem.pagamentos || [];
  const limitAtingido = pagamentos.length >= 3;

  const totalPago = pagamentos.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);

  const uploadNf = async (arquivo) => {
    setUploadingNf(true);
    try {
      const form = new FormData();
      form.append('arquivo', arquivo);
      const resp = await api.post(`/financeiro/${localItem.id}/upload-nf/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLocalItem(p => ({ ...p, nf_url: resp.data.nf_url, nf_anexada_em: resp.data.nf_anexada_em }));
      onUpdated();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao enviar NF.');
    } finally { setUploadingNf(false); }
  };

  const adicionarPagamento = async () => {
    if (!novoPag.valor || !novoPag.data_hora) {
      alert('Preencha valor e data/hora do pagamento.');
      return;
    }
    setSavingPag(true);
    try {
      const form = new FormData();
      form.append('valor', novoPag.valor);
      form.append('data_hora', novoPag.data_hora);
      if (novoPag.comprovante) form.append('comprovante', novoPag.comprovante);
      const resp = await api.post(`/financeiro/${localItem.id}/adicionar-pagamento/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Add new payment to local state
      const novoPagRecord = {
        id: resp.data.id,
        valor: resp.data.valor,
        data_hora: resp.data.data_hora,
        comprovante_url: resp.data.comprovante_url,
      };
      setLocalItem(p => ({ ...p, pagamentos: [...(p.pagamentos || []), novoPagRecord], valor_ja_pago: resp.data.valor_ja_pago }));
      setNovoPag({ valor: '', data_hora: '', comprovante: null });
      onUpdated();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao registrar pagamento.');
    } finally { setSavingPag(false); }
  };

  return (
    <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f6c90e' }}>Pagamentos</div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{localItem.cliente_nome}</div>
          </div>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }} onClick={onClose}><X size={18} /></button>
        </div>

        {/* Info do negócio */}
        <div style={S.infoBox}>
          <div style={S.infoRow}>
            <span style={{ fontSize: 12, color: '#64748b' }}>Faturamento Total</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#f6c90e' }}>{fmt(localItem.faturamento_total)}</span>
          </div>
          {localItem.venda_fechada_em && (
            <div style={S.infoRow}>
              <span style={{ fontSize: 12, color: '#64748b' }}>Data da Venda</span>
              <span style={{ fontSize: 13, color: '#cbd5e1' }}>{localItem.venda_fechada_em}</span>
            </div>
          )}
          {localItem.forma_pagamento_venda && (
            <div style={S.infoRow}>
              <span style={{ fontSize: 12, color: '#64748b' }}>Forma de Pagamento</span>
              <span style={{ fontSize: 13, color: '#cbd5e1' }}>{localItem.forma_pagamento_venda}</span>
            </div>
          )}
          {/* Links de documentos */}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {localItem.proposta_pdf_url && (
              <a href={localItem.proposta_pdf_url} target="_blank" rel="noopener noreferrer"
                style={{ ...S.btn('#60a5fa'), textDecoration: 'none' }}>
                <FileText size={13} /> Ver Proposta
              </a>
            )}
            {localItem.contrato_pdf_url && (
              <a href={localItem.contrato_pdf_url} target="_blank" rel="noopener noreferrer"
                style={{ ...S.btn('#c084fc'), textDecoration: 'none' }}>
                <File size={13} /> Ver Contrato
              </a>
            )}
          </div>
        </div>

        {/* NF */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Nota Fiscal</div>
          {localItem.nf_url ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle size={15} color="#4ade80" />
              <a href={localItem.nf_url} target="_blank" rel="noopener noreferrer"
                style={{ ...S.btn('#4ade80'), textDecoration: 'none' }}>
                <File size={13} /> Ver NF
              </a>
              {localItem.nf_anexada_em && (
                <span style={{ fontSize: 11, color: '#475569' }}>anexada em {localItem.nf_anexada_em}</span>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                style={S.btn('#f6c90e', uploadingNf)}
                onClick={() => !uploadingNf && nfInputRef.current.click()}
                disabled={uploadingNf}
              >
                <Upload size={13} /> {uploadingNf ? 'Enviando...' : 'Anexar NF'}
              </button>
              <span style={{ fontSize: 11, color: '#475569' }}>Após anexada não pode ser removida</span>
              <input ref={nfInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) uploadNf(e.target.files[0]); e.target.value = ''; }} />
            </div>
          )}
        </div>

        <hr style={S.divider} />

        {/* Pagamentos existentes */}
        <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
          Pagamentos registrados {pagamentos.length > 0 && `(${pagamentos.length}/3)`}
        </div>

        {pagamentos.length === 0 && (
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>Nenhum pagamento registrado ainda.</div>
        )}

        {pagamentos.map((p, i) => (
          <div key={p.id || i} style={S.payRow}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#4ade80' }}>{fmt(p.valor)}</span>
                <span style={{ fontSize: 12, color: '#475569', marginLeft: 10 }}>
                  {p.data_hora}
                </span>
              </div>
              {p.comprovante_url && (
                <a href={p.comprovante_url} target="_blank" rel="noopener noreferrer"
                  style={{ ...S.btn('#60a5fa'), textDecoration: 'none', fontSize: 11 }}>
                  <File size={12} /> Ver Comprovante
                </a>
              )}
            </div>
          </div>
        ))}

        {/* Total */}
        {pagamentos.length > 0 && (
          <div style={{ ...S.totalBox, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Total Pago</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#4ade80' }}>{fmt(totalPago)}</span>
          </div>
        )}

        {/* Novo Pagamento */}
        {limitAtingido ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 8, marginTop: 12 }}>
            <AlertTriangle size={15} color="#f87171" />
            <span style={{ fontSize: 12, color: '#f87171' }}>Limite de pagamentos atingido (3 registros).</span>
          </div>
        ) : (
          <div style={S.newPayBox}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f6c90e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Plus size={15} /> Novo Pagamento
            </div>
            <div style={S.row2}>
              <div>
                <label style={S.label}>Valor (R$)</label>
                <input
                  style={S.input} type="number" step="0.01" min="0"
                  placeholder="0,00"
                  value={novoPag.valor}
                  onChange={e => setNovoPag(p => ({ ...p, valor: e.target.value }))}
                  onWheel={e => e.target.blur()}
                />
              </div>
              <div>
                <label style={S.label}>Data e Hora</label>
                <input
                  style={S.input} type="datetime-local"
                  value={novoPag.data_hora}
                  onChange={e => setNovoPag(p => ({ ...p, data_hora: e.target.value }))}
                />
              </div>
            </div>
            {/* Comprovante (opcional) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <button
                style={S.btn('#64748b')}
                onClick={() => compInputRef.current.click()}
              >
                <Upload size={13} /> {novoPag.comprovante ? novoPag.comprovante.name : 'Comprovante (opcional)'}
              </button>
              {novoPag.comprovante && (
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: 11 }}
                  onClick={() => setNovoPag(p => ({ ...p, comprovante: null }))}>✕ remover</button>
              )}
              <input ref={compInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) setNovoPag(p => ({ ...p, comprovante: e.target.files[0] })); e.target.value = ''; }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                style={{ padding: '9px 22px', borderRadius: 8, background: 'rgba(246,201,14,0.15)', border: '1px solid rgba(246,201,14,0.3)', color: '#f6c90e', cursor: savingPag ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: savingPag ? 0.6 : 1 }}
                onClick={adicionarPagamento}
                disabled={savingPag}
              >
                {savingPag ? 'Salvando...' : 'Salvar Pagamento'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────────── */
export default function Financeiro() {
  const navigate = useNavigate();
  const location = useLocation();
  const focusId  = new URLSearchParams(location.search).get('id') ? parseInt(new URLSearchParams(location.search).get('id')) : null;
  const [items, setItems]           = useState([]);
  const [pagItem, setPagItem]       = useState(null);   // modal de pagamento
  const [custoItem, setCustoItem]   = useState(null);   // modal de custos
  const [lucroItem, setLucroItem]   = useState(null);   // modal de detalhe de lucro
  const [custoForm, setCustoForm]   = useState({});
  const [savingCusto, setSavingCusto] = useState(false);

  const [erro, setErro] = useState('');
  const loadData = () => {
    setErro('');
    api.get('/financeiro/').then(r => setItems(r.data)).catch(() => setErro('Erro ao carregar dados. Tente atualizar a página.'));
  };
  useEffect(() => { loadData(); }, []);

  const fc = k => e => setCustoForm(p => ({ ...p, [k]: e.target.value }));

  // ── Modal de custos ────────────────────────────────────────────────────────
  const openCustos = (item) => {
    const init = {};
    CUSTO_FIELDS.forEach(({ key }) => { init[key] = item[key] != null ? item[key] : ''; });
    let cp = '';
    if (item.comissao_paga_em) {
      cp = item.comissao_paga_em.slice(0, 16);
    }
    init.comissao_paga_em = cp;
    setCustoForm(init);
    setCustoItem(item);
  };

  const saveCustos = async () => {
    setSavingCusto(true);
    try {
      const payload = {};
      CUSTO_FIELDS.forEach(({ key }) => {
        const v = custoForm[key];
        payload[key] = v !== '' && v != null ? parseFloat(String(v).replace(',', '.')) : null;
      });
      const comissaoCalc = custoItem.comissao_calculada || 0;
      const incluirComissao = !!custoForm.comissao_paga_em;
      payload.valor_custo_total = calcCustoTotal(custoForm, comissaoCalc, incluirComissao);
      payload.comissao_paga_em = custoForm.comissao_paga_em || null;
      await api.patch(`/financeiro/${custoItem.id}/`, payload);
      setCustoItem(null);
      loadData();
    } catch (e) {
      alert('Erro: ' + e.message);
    } finally {
      setSavingCusto(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const renderJaPago = (item) => {
    const val = item.valor_ja_pago || 0;
    if (val === 0) {
      return (
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: 12, padding: 0 }}
          onClick={() => setPagItem(item)}>
          Registrar
        </button>
      );
    }
    return (
      <button
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        onClick={() => setPagItem(item)}
        title="Clique para gerenciar pagamentos"
      >
        <span style={{ color: '#f6c90e', fontWeight: 600, fontSize: 13, textDecoration: 'underline', textDecorationColor: 'rgba(246,201,14,0.3)', textUnderlineOffset: 2 }}>
          {fmt(val)}
        </span>
      </button>
    );
  };

  /* ── Valores do mês atual ─────────────────────────────── */
  const _hoje = new Date();
  const _mesAtual = _hoje.getMonth();
  const _anoAtual = _hoje.getFullYear();

  const calcAtual = (item) => {
    // Pago Atual: soma dos pagamentos registrados neste mês
    const pagoAtual = (item.pagamentos || []).reduce((s, p) => {
      if (!p.data_hora) return s;
      const d = new Date(p.data_hora);
      if (d.getMonth() === _mesAtual && d.getFullYear() === _anoAtual)
        return s + (parseFloat(p.valor) || 0);
      return s;
    }, 0);

    // Faturado Atual: faturamento_total se venda fechou neste mês (formato dd/mm/yyyy)
    let faturadoAtual = 0;
    if (item.venda_fechada_em) {
      const parts = item.venda_fechada_em.split('/');
      if (parts.length === 3) {
        const d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        if (d.getMonth() === _mesAtual && d.getFullYear() === _anoAtual)
          faturadoAtual = item.faturamento_total || 0;
      }
    }

    // Lucro Atual: pago atual − custo total (se houver custo cadastrado)
    const custo = item.valor_custo_total;
    const lucroAtual = pagoAtual > 0 && custo != null ? pagoAtual - custo : null;

    return { faturadoAtual, pagoAtual, lucroAtual };
  };

  /* ── Custo Atual: soma dos custos com data de pagamento marcada ─ */
  const CUSTO_CAMPOS = [
    'custo_equipamento','custo_instalacao','custo_indicacao','custo_logistica',
    'custo_material_eletrico','custo_crt','custo_padrao','custo_brinde'
  ];
  const calcCustoAtual = (item) => CUSTO_CAMPOS.reduce((s, c) => {
    if (item[`${c}_pago_em`] && item[c] != null) return s + parseFloat(item[c]);
    return s;
  }, 0);

  /* ── Lucro Total (sobre faturamento_total vs custo_total) ─ */
  const calcLucroTotal = (item) => {
    const fat  = item.faturamento_total;
    const custo = item.valor_custo_total;
    if (!fat || fat === 0 || custo == null) return null;
    const valor = fat - custo;
    const pct   = (valor / fat) * 100;
    return { valor, pct };
  };

  /* ── Lucro Atual (sobre valor_ja_pago vs custo_atual) ─ */
  const calcLucroAtual = (item) => {
    const fat   = item.valor_ja_pago || 0;
    const custo = calcCustoAtual(item);
    if (fat === 0 || custo === 0) return null;
    const valor = fat - custo;
    const pct   = (valor / fat) * 100;
    return { valor, pct };
  };

  /* ── (mantido para compatibilidade interna — modal detalhe de lucro) ─ */
  const calcLucro = calcLucroTotal;

  const renderLucro = (item) => {
    const l = calcLucro(item);
    if (!l) return <span style={{ color: '#475569' }}>—</span>;
    const cor = l.pct >= 0 ? '#4ade80' : '#f87171';
    return (
      <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        onClick={() => setLucroItem(item)} title="Ver detalhes do cálculo">
        <span style={{ color: cor, fontWeight: 700, textDecoration: 'underline', textDecorationColor: cor + '55', textUnderlineOffset: 2 }}>
          {l.pct.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}%
        </span>
      </button>
    );
  };

  const renderLucroValor = (item) => {
    const l = calcLucro(item);
    if (!l) return <span style={{ color: '#475569' }}>—</span>;
    const cor = l.valor >= 0 ? '#4ade80' : '#f87171';
    return (
      <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        onClick={() => setLucroItem(item)} title="Ver detalhes do cálculo">
        <span style={{ color: cor, fontWeight: 700, textDecoration: 'underline', textDecorationColor: cor + '55', textUnderlineOffset: 2 }}>
          {fmt(l.valor)}
        </span>
      </button>
    );
  };

  const renderCustoTotal = (item) => {
    if (item.valor_custo_total != null) {
      return (
        <button style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 0, fontSize: 13, textDecoration: 'underline', textDecorationColor: 'rgba(248,113,113,0.3)', textUnderlineOffset: 2 }}
          onClick={() => navigate(`/custos-comercial?id=${item.id}`)} title="Ver Custos Comercial">
          {fmt(item.valor_custo_total)}
        </button>
      );
    }
    return (
      <button style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline', textDecorationColor: 'rgba(71,85,105,0.3)', textUnderlineOffset: 2 }}
        onClick={() => openCustos(item)}>
        + Adicionar custos
      </button>
    );
  };

  const renderComissao = (item) => {
    if (!item.comissao_calculada) return <span style={{ color: '#475569' }}>—</span>;
    const pago = item.comissao_paga_em;
    return (
      <div>
        <span style={{ color: pago ? '#4ade80' : '#f6c90e', fontWeight: 600 }}>{fmt(item.comissao_calculada)}</span>
        <div style={{ fontSize: 10, color: pago ? '#4ade80' : '#f87171', marginTop: 2 }}>
          {pago ? `✅ paga ${new Date(pago).toLocaleDateString('pt-BR')}` : '⏳ pendente'}
        </div>
      </div>
    );
  };

  const custoTotalPreview = custoItem
    ? calcCustoTotal(custoForm, custoItem.comissao_calculada || 0, !!custoForm.comissao_paga_em)
    : 0;

  return (
    <div>
      <div style={S.heading}>Financeiro Comercial</div>
      <div style={S.sub}>Controle de pagamentos das vendas fechadas</div>

      {erro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{erro}</div>
      )}

      {(() => {
        const totalFat      = items.reduce((s, i) => s + (i.faturamento_total   ?? 0), 0);
        const totalCusto    = items.reduce((s, i) => s + (i.valor_custo_total   ?? 0), 0);
        const totalLucroT   = items.reduce((s, i) => { const l = calcLucroTotal(i); return l ? s + l.valor : s; }, 0);
        const totalFatAtual = items.reduce((s, i) => s + (i.valor_ja_pago       ?? 0), 0);
        const totalCustoAt  = items.reduce((s, i) => s + calcCustoAtual(i), 0);
        const totalLucroA   = items.reduce((s, i) => { const l = calcLucroAtual(i); return l ? s + l.valor : s; }, 0);
        const hasLucroT     = items.some(i => calcLucroTotal(i) != null);
        const hasLucroA     = items.some(i => calcLucroAtual(i) != null);
        const lucroTPct     = totalFat > 0 ? ((totalLucroT / totalFat) * 100).toLocaleString('pt-BR', { minimumFractionDigits:1 }) + '%' : null;
        const lucroAPct     = totalFatAtual > 0 ? ((totalLucroA / totalFatAtual) * 100).toLocaleString('pt-BR', { minimumFractionDigits:1 }) + '%' : null;

        const tfTd = (extra = {}) => ({
          padding: '11px 14px', fontSize: 12, fontWeight: 700,
          color: '#94a3b8', background: '#080e1a',
          borderTop: '2px solid #1a2035', whiteSpace: 'nowrap', ...extra,
        });

        const renderPct = (l, it, tipo) => {
          if (!l) return <span style={{ color:'#475569' }}>—</span>;
          const cor = l.pct >= 0 ? '#4ade80' : '#f87171';
          return (
            <button style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}
              onClick={() => setLucroItem({ ...it, _lucroTipo: tipo })}
              title="Ver detalhes do cálculo">
              <span style={{ color:cor, fontWeight:700, textDecoration:'underline', textDecorationColor:cor+'55', textUnderlineOffset:2 }}>
                {l.pct.toLocaleString('pt-BR',{minimumFractionDigits:1})}%
              </span>
            </button>
          );
        };
        const renderVal = (l, it, tipo) => {
          if (!l) return <span style={{ color:'#475569' }}>—</span>;
          const cor = l.valor >= 0 ? '#4ade80' : '#f87171';
          return (
            <button style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}
              onClick={() => setLucroItem({ ...it, _lucroTipo: tipo })}
              title="Ver detalhes do cálculo">
              <span style={{ color:cor, fontWeight:700, textDecoration:'underline', textDecorationColor:cor+'55', textUnderlineOffset:2 }}>
                {fmt(l.valor)}
              </span>
            </button>
          );
        };

        return (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Cliente</th>
                  <th style={S.th}>Vendedor</th>
                  <th style={S.th}>Faturamento Total</th>
                  <th style={S.th}>Custo Total</th>
                  <th style={S.th}>Lucro Total %</th>
                  <th style={S.th}>Lucro Total R$</th>
                  <th style={S.th}>Faturamento Atual</th>
                  <th style={S.th}>Custo Atual</th>
                  <th style={S.th}>Lucro Atual %</th>
                  <th style={S.th}>Lucro Atual R$</th>
                  <th style={S.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const lTotal  = calcLucroTotal(item);
                  const lAtual  = calcLucroAtual(item);
                  const custoAt = calcCustoAtual(item);
                  return (
                    <tr key={item.id}
                      style={item.id === focusId ? { background: 'rgba(246,201,14,0.06)', outline: '1px solid rgba(246,201,14,0.3)' } : {}}
                      onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                      onMouseLeave={e => e.currentTarget.style.background = item.id === focusId ? 'rgba(246,201,14,0.06)' : ''}>
                      <td style={S.td}><button style={linkStyle} onClick={() => navigate(`/jornada/${item.cliente}`)}>{item.cliente_nome}</button></td>
                      <td style={S.td}>{item.vendedor_nome}</td>
                      <td style={S.td}><span style={{ color:'#f6c90e', fontWeight:700 }}>{item.faturamento_total != null ? fmt(item.faturamento_total) : '—'}</span></td>
                      <td style={S.td}>{renderCustoTotal(item)}</td>
                      <td style={S.td}>{renderPct(lTotal, item, 'total')}</td>
                      <td style={S.td}>{renderVal(lTotal, item, 'total')}</td>
                      <td style={S.td}>{renderJaPago(item)}</td>
                      <td style={S.td}>{custoAt > 0 ? <span style={{ color:'#f87171', fontWeight:600 }}>{fmt(custoAt)}</span> : <span style={{ color:'#475569' }}>R$ 0,00</span>}</td>
                      <td style={S.td}>{renderPct(lAtual, item, 'atual')}</td>
                      <td style={S.td}>{renderVal(lAtual, item, 'atual')}</td>
                      <td style={S.td}><span style={S.tag(STATUS_COLOR[item.status] || '#64748b')}>{STATUS_LABEL[item.status] || item.status?.replace(/_/g,' ')}</span></td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan={11} style={{ ...S.td, textAlign:'center', color:'#475569', padding:32 }}>Nenhum registro financeiro</td></tr>
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr>
                    <td style={tfTd({ color:'#f6c90e', fontSize:11, textTransform:'uppercase', letterSpacing:1 })} colSpan={2}>
                      Total ({items.length} {items.length === 1 ? 'registro' : 'registros'})
                    </td>
                    <td style={tfTd({ color:'#f6c90e' })}>{fmt(totalFat)}</td>
                    <td style={tfTd()}>{totalCusto > 0 ? fmt(totalCusto) : '—'}</td>
                    <td style={tfTd({ color: hasLucroT ? (totalLucroT >= 0 ? '#4ade80' : '#f87171') : '#475569' })}>
                      {hasLucroT && lucroTPct ? lucroTPct : '—'}
                    </td>
                    <td style={tfTd({ color: hasLucroT ? (totalLucroT >= 0 ? '#4ade80' : '#f87171') : '#475569' })}>
                      {hasLucroT ? fmt(totalLucroT) : '—'}
                    </td>
                    <td style={tfTd({ color:'#4ade80' })}>{fmt(totalFatAtual)}</td>
                    <td style={tfTd()}>{totalCustoAt > 0 ? fmt(totalCustoAt) : '—'}</td>
                    <td style={tfTd({ color: hasLucroA ? (totalLucroA >= 0 ? '#4ade80' : '#f87171') : '#475569' })}>
                      {hasLucroA && lucroAPct ? lucroAPct : '—'}
                    </td>
                    <td style={tfTd({ color: hasLucroA ? (totalLucroA >= 0 ? '#4ade80' : '#f87171') : '#475569' })}>
                      {hasLucroA ? fmt(totalLucroA) : '—'}
                    </td>
                    <td style={tfTd()} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        );
      })()}

      {/* ── Modal detalhe de lucro ─────────────────────────────────────── */}
      {lucroItem && (() => {
        const tipo   = lucroItem._lucroTipo || 'total';
        const isTotal = tipo === 'total';
        const fat    = isTotal ? lucroItem.faturamento_total : (lucroItem.valor_ja_pago || 0);
        const custo  = isTotal ? lucroItem.valor_custo_total : calcCustoAtual(lucroItem);
        const lucro  = isTotal ? calcLucroTotal(lucroItem) : calcLucroAtual(lucroItem);
        const titulo = isTotal ? 'Lucro Total' : 'Lucro Atual';
        const fatLabel  = isTotal ? 'Faturamento Total' : 'Faturamento Atual';
        const custoLabel = isTotal ? 'Custo Total' : 'Custo Atual (custos pagos)';
        const onClose = () => setLucroItem(null);
        const row = (label, val, cor = '#e2e8f0', sinal = '') => (
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #0f1117' }}>
            <span style={{ fontSize:13, color:'#94a3b8' }}>{label}</span>
            <span style={{ fontSize:13, fontWeight:700, color:cor }}>{sinal}{val}</span>
          </div>
        );
        return (
          <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ ...S.modal, minWidth:340, maxWidth:420 }}>
              <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:4 }}>{titulo}</div>
              <div style={{ fontSize:13, color:'#475569', marginBottom:16 }}>
                Cliente: <span style={{ color:'#e2e8f0', fontWeight:600 }}>{lucroItem.cliente_nome}</span>
              </div>
              {lucro ? (
                <>
                  {row(fatLabel,    fmt(fat),   isTotal ? '#f6c90e' : '#f6c90e')}
                  {row(custoLabel,  custo != null ? fmt(custo) : '—', '#f87171', '−')}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
                    <span style={{ fontSize:13, color:'#94a3b8' }}>Lucro R$</span>
                    <span style={{ fontSize:16, fontWeight:800, color: lucro.valor >= 0 ? '#4ade80' : '#f87171' }}>
                      {fmt(lucro.valor)}
                    </span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0' }}>
                    <span style={{ fontSize:13, color:'#94a3b8' }}>Lucro %</span>
                    <span style={{ fontSize:14, fontWeight:700, color: lucro.pct >= 0 ? '#4ade80' : '#f87171' }}>
                      {lucro.pct.toLocaleString('pt-BR', { minimumFractionDigits:1 })}%
                    </span>
                  </div>
                  <div style={{ fontSize:11, color:'#475569', marginTop:10, padding:'8px 10px', background:'#080c14', borderRadius:6 }}>
                    Fórmula: ({fatLabel} − {custoLabel}) ÷ {fatLabel} × 100
                  </div>
                </>
              ) : (
                <div style={{ color:'#475569', fontSize:13, textAlign:'center', padding:20 }}>
                  Dados insuficientes para calcular {titulo.toLowerCase()}.
                </div>
              )}
              <button style={{ marginTop:16, width:'100%', padding:'9px 0', borderRadius:8, background:'#1a2035', border:'none', color:'#94a3b8', cursor:'pointer', fontSize:13 }} onClick={onClose}>Fechar</button>
            </div>
          </div>
        );
      })()}

      {/* ── 
      {/* ── Modal de Pagamento ────────────────────────────────────────────────────── */}
      {pagItem && (
        <PagamentoModal
          item={pagItem}
          onClose={() => setPagItem(null)}
          onUpdated={() => { loadData(); }}
        />
      )}

      {/* ── Modal de Custos ───────────────────────────────────────────────────────── */}
      {custoItem && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setCustoItem(null); }}>
          <div style={{ ...S.modal, minWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#f6c90e' }}>Custos Comercial</div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{custoItem.cliente_nome}</div>
              </div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569' }} onClick={() => setCustoItem(null)}><X size={18} /></button>
            </div>
            <div style={S.row2}>
              {CUSTO_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label style={S.label}>{label}</label>
                  <input style={S.input} type="number" step="0.01" placeholder="0,00"
                    value={custoForm[key] ?? ''} onChange={fc(key)} />
                </div>
              ))}
            </div>
            {custoItem.comissao_calculada > 0 && (
              <div style={S.infoBox}>
                <div style={{ ...S.infoRow, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>Comissão calculada</span>
                  <span style={{ fontWeight: 700, color: '#f6c90e' }}>{fmt(custoItem.comissao_calculada)}</span>
                </div>
                <label style={S.label}>Data pagamento comissão (deixe vazio se ainda não foi paga)</label>
                <input style={S.input} type="datetime-local"
                  value={custoForm.comissao_paga_em ?? ''}
                  onChange={fc('comissao_paga_em')} />
              </div>
            )}
            <div style={S.totalBox}>
              <span style={{ fontSize: 13, color: '#94a3b8' }}>Total Custos</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#f87171' }}>{fmt(custoTotalPreview)}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={{ padding: '9px 18px', borderRadius: 8, background: '#1a2035', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }} onClick={() => setCustoItem(null)}>Cancelar</button>
              <button
                style={{ padding: '9px 22px', borderRadius: 8, background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', cursor: savingCusto ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, opacity: savingCusto ? 0.6 : 1 }}
                onClick={saveCustos}
                disabled={savingCusto}>
                {savingCusto ? 'Salvando...' : 'Salvar Custos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
